import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack } from '@mui/material';

import useApi from 'hooks/useApi';
import { errorMessage, unwrapEnvelope } from 'components/legal/legalUtils';
import { ClientLegalOverview } from 'types/legal';
import ConsentSettings, { normalizeConsentSettings } from './ConsentSettings';
import LegalDocumentsCard from './LegalDocumentsCard';
import MissingConsentCard from './MissingConsentCard';
import ConsentReport from './ConsentReport';

interface LegalConsentTabProps {
  clientUid: string;
  clientName?: string;
}

// Clients → "Legal & Consent". Rendered only for a Super Admin (GET legal/access); every
// endpoint used here re-checks that server-side.
const LegalConsentTab = ({ clientUid, clientName }: LegalConsentTabProps) => {
  const { getClientLegal } = useApi();
  const fetchRef = useRef(getClientLegal);
  fetchRef.current = getClientLegal;
  const requestId = useRef(0);

  const [overview, setOverview] = useState<ClientLegalOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'settings' | 'report'>('settings');

  // `silent` keeps what's on screen while refreshing counts after a save or publish.
  const load = useCallback(
    (silent = false) => {
      const ticket = ++requestId.current;
      if (!silent) setLoading(true);
      setError(null);
      fetchRef
        .current(clientUid)
        .then((response: any) => {
          if (ticket === requestId.current) setOverview(unwrapEnvelope<ClientLegalOverview>(response));
        })
        .catch((err: any) => {
          if (ticket === requestId.current) setError(errorMessage(err, 'Could not load legal settings.'));
        })
        .finally(() => {
          if (ticket === requestId.current) setLoading(false);
        });
    },
    [clientUid]
  );

  useEffect(() => {
    setOverview(null);
    setView('settings');
    load();
  }, [load]);

  const refresh = useCallback(() => load(true), [load]);
  const name = overview?.client?.name || clientName || 'this client';

  if (loading && !overview) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress aria-label="Loading legal settings" />
      </Box>
    );
  }

  if (!overview) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => load()}>
              Retry
            </Button>
          }
        >
          {error || 'Could not load legal settings.'}
        </Alert>
      </Box>
    );
  }

  if (view === 'report') {
    return (
      <Box sx={{ p: 3 }}>
        <ConsentReport
          clientUid={clientUid}
          clientName={name}
          onBack={() => {
            setView('settings');
            refresh();
          }}
        />
      </Box>
    );
  }

  const consent = normalizeConsentSettings(overview.consent);

  return (
    <Stack spacing={3} sx={{ p: 3 }}>
      {error && <Alert severity="error">{error}</Alert>}
      <ConsentSettings
        clientUid={clientUid}
        consent={overview.consent}
        documentsPublished={Boolean(overview.documents?.PRIVACY_POLICY?.current && overview.documents?.TERMS?.current)}
        onSaved={refresh}
      />
      <LegalDocumentsCard clientUid={clientUid} clientName={name} slots={overview.documents ?? {}} onPublished={refresh} />
      <MissingConsentCard
        clientUid={clientUid}
        clientName={name}
        consent={consent}
        missing={overview.missing}
        onViewReport={() => setView('report')}
        onSignedOut={refresh}
      />
    </Stack>
  );
};

export default LegalConsentTab;
